import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import PortalLayout from "@/components/PortalLayout";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ArrowLeft, Send, Timer, Paperclip, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import RichTextEditor, { RichTextDisplay } from "@/components/RichTextEditor";

const STAGE_STATUS_COLORS = {
  todo: { bg: "#F5F3EF", text: "#57534E" },
  in_progress: { bg: "#DBEAFE", text: "#1D4ED8" },
  in_review: { bg: "#FEF3C7", text: "#D97706" },
  completed: { bg: "#D1FAE5", text: "#059669" },
};
const STAGE_STATUS_FMT = { todo: "To Do", in_progress: "In Progress", in_review: "In Review", completed: "Completed" };

const statusColors = {
  pending_review: { bg: "#FEF3C7", text: "#D97706" },
  in_progress: { bg: "#DBEAFE", text: "#1D4ED8" },
  completed: { bg: "#D1FAE5", text: "#059669" },
  cancelled: { bg: "#FEE2E2", text: "#DC2626" },
  on_hold: { bg: "#F3E8FF", text: "#7C3AED" },
};

export default function PortalRequestDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [request, setRequest] = useState(null);
  const [messages, setMessages] = useState([]);
  const [stages, setStages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msgText, setMsgText] = useState("");
  const [msgAttachments, setMsgAttachments] = useState([]);
  const msgFileRef = useRef(null);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    Promise.all([
      api.get(`/portal/requests/${id}`),
      api.get(`/portal/requests/${id}/messages`),
    ])
      .then(([r, m]) => {
        setRequest(r.data);
        setMessages(m.data);
        setStages(r.data.stages || []);
      })
      .catch(() => navigate("/portal/requests"))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!msgText.trim() && msgAttachments.length === 0) return;
    setSending(true);
    try {
      const r = await api.post(`/portal/requests/${id}/messages`, { content: msgText, attachments: msgAttachments });
      setMessages((prev) => [...prev, r.data]);
      setMsgText("");
      setMsgAttachments([]);
    } catch {
      toast.error("Failed to send message");
    } finally {
      setSending(false);
    }
  };

  const handleMsgFile = (e) => {
    Array.from(e.target.files || []).forEach((file) => {
      if (file.size > 10 * 1024 * 1024) { toast.error(`${file.name} too large`); return; }
      const reader = new FileReader();
      reader.onload = (ev) => {
        const b64 = ev.target.result.split(",")[1];
        setMsgAttachments((p) => [...p, { filename: file.name, content_type: file.type, data_b64: b64 }]);
      };
      reader.readAsDataURL(file);
    });
    e.target.value = "";
  };

  if (loading) return <PortalLayout><div className="py-12 text-center text-sm" style={{ color: "#A8A29E" }}>Loading...</div></PortalLayout>;
  if (!request) return null;

  const sc = statusColors[request.status] || { bg: "#F5F3EF", text: "#57534E" };

  return (
    <PortalLayout>
      <div data-testid="portal-request-detail">
        {/* Header */}
        <div className="flex items-start gap-4 mb-6">
          <button onClick={() => navigate("/portal/requests")} className="mt-1 p-1.5 rounded-lg hover:bg-white" style={{ color: "#A8A29E" }}>
            <ArrowLeft size={18} strokeWidth={1.5} />
          </button>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <h1 className="font-serif text-2xl font-bold" style={{ color: "#292524" }}>{request.title}</h1>
              <span className="text-xs font-medium px-2.5 py-1 rounded-full" style={{ background: sc.bg, color: sc.text }}>{request.status?.replace(/_/g, " ")}</span>
            </div>
            <p className="text-sm" style={{ color: "#57534E" }}>Submitted {request.created_at?.slice(0, 10)}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Messages */}
          <div className="lg:col-span-2">
            <h2 className="font-serif text-base font-semibold mb-3" style={{ color: "#292524" }}>Messages</h2>
            <div className="space-y-4 mb-4 min-h-48 max-h-96 overflow-y-auto pr-1">
              {messages.length === 0 && (
                <p className="text-sm text-center py-8" style={{ color: "#A8A29E" }}>No messages yet. Start the conversation with Balbis.</p>
              )}
              {messages.map((msg) => {
                const isMe = msg.sender_type === "client";
                const initial = msg.sender_name?.[0]?.toUpperCase() || "?";
                return (
                  <div key={msg.id} className="flex gap-3 items-start">
                    <div
                      className="w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center text-white text-xs font-semibold mt-0.5"
                      style={{ background: isMe ? "#D97706" : "#292524" }}
                    >
                      {initial}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline gap-2 mb-1">
                        <span className="text-sm font-semibold" style={{ color: "#292524" }}>{msg.sender_name}</span>
                        <span className="text-xs" style={{ color: "#C7C3BF" }}>{msg.created_at?.slice(0, 16).replace("T", " ")}</span>
                      </div>
                      <div className="rounded-xl px-4 py-3" style={{ background: isMe ? "#FAFAF9" : "#fff", border: "1px solid #E7E5E4" }}>
                        <RichTextDisplay html={msg.content} />
                        {msg.attachments?.length > 0 && (
                          <div className="mt-2 space-y-1">
                            {msg.attachments.map((att, i) => (
                              <a
                                key={i}
                                href={`data:${att.content_type};base64,${att.data_b64}`}
                                download={att.filename}
                                className="flex items-center gap-1.5 text-xs px-2 py-1.5 rounded-lg hover:bg-stone-50"
                                style={{ color: "#0284C7", border: "1px solid #E7E5E4" }}
                              >
                                <Paperclip size={11} />{att.filename}
                              </a>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>
            {request.status !== "cancelled" && request.status !== "completed" && (
              <div className="space-y-2">
                <div className="flex items-start gap-2 px-3 py-2 rounded-lg text-xs" style={{ background: "#FEF3C7", color: "#92400E" }}>
                  <span className="mt-0.5">&#9888;</span>
                  <span>Messages on this request are visible to all team members in your organisation.</span>
                </div>
                {msgAttachments.length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {msgAttachments.map((att, i) => (
                      <div key={i} className="flex items-center gap-1 px-2 py-1 rounded-lg text-xs" style={{ background: "#F5F3EF", color: "#292524" }}>
                        <Paperclip size={10} />
                        <span className="max-w-[120px] truncate">{att.filename}</span>
                        <button onClick={() => setMsgAttachments((p) => p.filter((_, j) => j !== i))} style={{ color: "#A8A29E" }}><X size={10} /></button>
                      </div>
                    ))}
                  </div>
                )}
                <RichTextEditor value={msgText} onChange={setMsgText} placeholder="Write a message to Balbis..." minHeight={72} />
                <div className="flex items-center justify-between">
                  <div>
                    <input ref={msgFileRef} type="file" multiple className="hidden" onChange={handleMsgFile} />
                    <button
                      type="button"
                      onClick={() => msgFileRef.current?.click()}
                      className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border hover:bg-stone-50"
                      style={{ borderColor: "#E7E5E4", color: "#57534E" }}
                      data-testid="portal-attach-button"
                    >
                      <Paperclip size={12} />Attach
                    </button>
                  </div>
                  <Button
                    data-testid="portal-send-message-button"
                    size="sm"
                    disabled={sending || (!msgText.trim() && msgAttachments.length === 0)}
                    onClick={sendMessage}
                    style={{ background: "#D97706", color: "#fff" }}
                  >
                    <Send size={13} className="mr-1" />Send
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Details */}
          <div className="space-y-4">
            <div className="bg-white rounded-xl border p-5" style={{ borderColor: "#E7E5E4" }}>
              <p className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: "#A8A29E" }}>Task Details</p>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span style={{ color: "#57534E" }}>Status</span>
                  <span className="text-xs font-medium px-2 py-0.5 rounded-full" style={{ background: sc.bg, color: sc.text }}>{request.status?.replace(/_/g, " ")}</span>
                </div>
                {request.priority && (
                  <div className="flex justify-between">
                    <span style={{ color: "#57534E" }}>Priority</span>
                    <span className="capitalize font-medium" style={{ color: "#292524" }}>{request.priority}</span>
                  </div>
                )}
                {request.time_estimate_hours && (
                  <div className="flex justify-between">
                    <span style={{ color: "#57534E" }}>Estimated</span>
                    <span style={{ color: "#292524" }}>{request.time_estimate_hours}h</span>
                  </div>
                )}
                {request.assigned_to_name && (
                  <div className="flex justify-between">
                    <span style={{ color: "#57534E" }}>Assigned to</span>
                    <span style={{ color: "#292524" }}>{request.assigned_to_name}</span>
                  </div>
                )}
              </div>
            </div>

            {stages.length > 0 && (
              <div className="bg-white rounded-xl border p-5" style={{ borderColor: "#E7E5E4" }} data-testid="portal-stages-section">
                <p className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: "#A8A29E" }}>
                  Stages ({stages.length})
                </p>
                <div className="space-y-2">
                  {stages.sort((a, b) => (a.order ?? 0) - (b.order ?? 0)).map((stage) => {
                    const sc2 = STAGE_STATUS_COLORS[stage.status] || STAGE_STATUS_COLORS.todo;
                    return (
                      <div key={stage.id} className="flex items-center gap-2" data-testid={`portal-stage-${stage.id}`}>
                        <span
                          className="text-xs px-2 py-0.5 rounded-full font-medium flex-shrink-0"
                          style={{ background: sc2.bg, color: sc2.text }}
                        >
                          {STAGE_STATUS_FMT[stage.status] || stage.status}
                        </span>
                        <span className="text-sm flex-1 truncate" style={{ color: "#292524" }}>{stage.title}</span>
                        {stage.time_estimate > 0 && (
                          <span className="flex items-center gap-0.5 text-xs flex-shrink-0" style={{ color: "#A8A29E" }}>
                            <Timer size={10} />
                            {stage.time_estimate}h
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="bg-white rounded-xl border p-5" style={{ borderColor: "#E7E5E4" }}>
              <p className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: "#A8A29E" }}>Description</p>
              <p className="text-sm leading-relaxed" style={{ color: "#57534E" }}>{request.description}</p>
            </div>
          </div>
        </div>
      </div>
    </PortalLayout>
  );
}
